import { useState } from "react";
import { useHistory } from "react-router-dom";

import { saveRestTokenPair } from "apiCommandsExecutor/token";
import {
    requestSignInCode,
    signInWithCode,
    signInWithPassword,
} from "services/auth/rest";
import { TokenPairDto } from "services/auth/restModels";
import { useSession } from "shared/hooks/useSession";
import { useNotificationSnackbar } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

import { HOME_PATH, PHONE_DIGITS_LENGTH, SIGN_IN_CODE_LENGTH } from "../constants";
import { SignInMethod, SignInStep } from "../types";

export const useSignInState = () => {
    const history = useHistory();
    const { refresh } = useSession();
    const notify = useNotificationSnackbar();

    const [method, setMethod] = useState<SignInMethod>("code");
    const [step, setStep] = useState<SignInStep>("credentials");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [password, setPassword] = useState("");
    const [code, setCode] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const isPhoneComplete = phoneNumber.length === PHONE_DIGITS_LENGTH;

    /*
    * Токен кладём до обновления сессии: запрос за аккаунтом уходит уже с ним, и переход на
    * главную происходит по загруженной сессии, а не в надежде, что она подтянется следом.
    * */
    const completeSignIn = async (tokenPair: TokenPairDto) => {
        saveRestTokenPair(tokenPair);

        const account = await refresh();

        if (!account) {
            throw new Error("Не удалось загрузить данные аккаунта");
        }

        history.push(HOME_PATH);
    };

    const run = async (action: () => Promise<void>, failureTitle: string) => {
        setSubmitting(true);

        try {
            await action();
        } catch (error) {
            notify.showError(failureTitle, error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestCode = () =>
        run(async () => {
            await requestSignInCode({ phoneNumber });

            setCode("");
            setStep("code");
        }, "Не удалось отправить код");

    const handleSubmitCode = (value: string) =>
        run(async () => {
            const tokenPair = await signInWithCode({ phoneNumber, code: value });

            await completeSignIn(tokenPair);
        }, "Не удалось войти");

    const handleSubmitPassword = () =>
        run(async () => {
            const tokenPair = await signInWithPassword({ phoneNumber, password });

            await completeSignIn(tokenPair);
        }, "Не удалось войти");

    const handleChangeMethod = (next: SignInMethod) => {
        setMethod(next);
        setStep("credentials");
        setCode("");
        setPassword("");
    };

    const handleChangeCode = (value: string) => {
        setCode(value);
    };

    return {
        method,
        step,
        phoneNumber,
        password,
        code,
        submitting,
        isPhoneComplete,
        canSubmitPassword: isPhoneComplete && password.length > 0,
        notify,
        setPhoneNumber,
        setPassword,
        onChangeMethod: handleChangeMethod,
        onChangeCode: handleChangeCode,
        onRequestCode: handleRequestCode,
        onSubmitCode: handleSubmitCode,
        onSubmitPassword: handleSubmitPassword,
        onBackToCredentials: () => setStep("credentials"),
        codeLength: SIGN_IN_CODE_LENGTH,
    };
};
